import { useEffect, useId, useRef, useState } from 'react';

type DocumentationMermaidDiagramProps = {
  chart: string;
};

let mermaidReady: Promise<typeof import('mermaid').default> | null = null;

async function getMermaid() {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((mod) => {
      const mermaid = mod.default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'loose',
        theme: 'neutral',
        fontFamily: 'inherit',
        flowchart: {
          htmlLabels: true,
          curve: 'basis',
        },
      });
      return mermaid;
    });
  }
  return mermaidReady;
}

/** Evita SVG com height=100% colapsar para 0 no layout da wiki. */
function normalizeMermaidSvg(svg: string): string {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return svg;
  }

  try {
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
    const el = doc.documentElement;
    if (!el || el.tagName.toLowerCase() !== 'svg') {
      return svg;
    }

    const height = el.getAttribute('height');
    const viewBox = el.getAttribute('viewBox');
    if ((height === '100%' || height === '0' || !height) && viewBox) {
      const parts = viewBox.trim().split(/[\s,]+/);
      if (parts.length === 4) {
        const w = parts[2];
        const h = parts[3];
        if (w && h) {
          el.setAttribute('width', w);
          el.setAttribute('height', h);
          el.removeAttribute('style');
        }
      }
    }

    el.setAttribute('role', 'img');
    return new XMLSerializer().serializeToString(el);
  } catch {
    return svg;
  }
}

/**
 * Renderiza bloco ```mermaid no markdown da documentação.
 */
export function DocumentationMermaidDiagram({ chart }: DocumentationMermaidDiagramProps) {
  const reactId = useId().replace(/:/g, '');
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const source = chart.trim();
    const container = containerRef.current;

    if (!source) {
      setLoading(false);
      setError(null);
      if (container) container.innerHTML = '';
      return;
    }

    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const mermaid = await getMermaid();
        const renderId = `docs-mermaid-${reactId}-${Math.random().toString(36).slice(2, 9)}`;
        const result = await mermaid.render(renderId, source);
        if (cancelled) return;

        const svg = normalizeMermaidSvg(result.svg);
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
        setError(null);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        setError(err instanceof Error ? err.message : 'Falha ao renderizar diagrama.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chart, reactId]);

  if (error) {
    return (
      <div className="docs-md-mermaid docs-md-mermaid--error" role="alert">
        <p className="docs-md-mermaid__error-label">Diagrama Mermaid inválido</p>
        <pre className="docs-md-prose__pre">{chart}</pre>
        <p className="docs-md-mermaid__error-detail">{error}</p>
      </div>
    );
  }

  return (
    <div className={`docs-md-mermaid${loading ? ' docs-md-mermaid--loading' : ''}`}>
      {loading ? <span className="docs-md-mermaid__loading-label">Carregando diagrama…</span> : null}
      <div ref={containerRef} className="docs-md-mermaid__canvas" hidden={loading} />
    </div>
  );
}
